import { CheckCircle, XCircle } from 'lucide-react';
import { DynamicIcon, type IconName } from 'lucide-react/dynamic';
import { DateTime } from 'luxon';
import React, { useState } from 'react';
import { Card } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import {
  type Card as CardType,
  RecurrenceTaskStatusEnum,
} from '../../service/api';
import useTasksStore, { type TaskItem } from '../../store/tasks';
import CardActionsList from '../CardActionsList';
import ProgressBar from '../ProgressBar';
import TaskDetailsModal from '../TaskDetailsModal';

interface CompletedTaskCardProps {
  task: TaskItem;
}

interface ActiveTaskCardProps {
  task: TaskItem;
  icon: IconName;
}

interface FailedTaskCardProps {
  task: TaskItem;
}

interface TaskCardProps {
  /** Task unique ID */
  taskId: string;
  /**
   * Lucide icon for the task
   * Example: import { Book, Broom } from "lucide-react";
   */
  icon: IconName;
}

const ClarificationSpan = (element: string | React.JSX.Element) => (
  <span
    className="position-absolute top-100 translate-middle-y me-2 small text-white px-2 py-1 rounded"
    style={{
      right: 0,
      backgroundColor: 'rgba(23, 23, 23, 0.6)',
    }}
  >
    {element}
  </span>
);

const CompletedTaskCard: React.FC<CompletedTaskCardProps> = ({ task }) => {
  const { title, rewardCard, status } = task;
  const isPendingApproval = status === RecurrenceTaskStatusEnum.CompleteRequest;
  const { t } = useTranslation();

  const waitingApproval = ClarificationSpan(t('tasks.waitingApproval'));

  return (
    <Card
      text="white"
      className="mb-3"
      style={{
        backgroundColor: 'rgba(6, 56, 96, 0.85)',
        opacity: isPendingApproval ? 0.7 : 1,
      }}
    >
      <Card.Body className="d-flex gap-3">
        <div
          className="d-flex align-items-center justify-content-center rounded-3 bg-primary"
          style={{ width: '3rem', height: '3rem', flexShrink: 0 }}
        >
          <CheckCircle size={32} strokeWidth={2} />
        </div>
        <div className="flex-grow-1 d-flex flex-column justify-content-center">
          <h3 className="h5 fw-bold mb-0 text-decoration-line-through">
            {title}
          </h3>
          {isPendingApproval && waitingApproval}
        </div>
        {typeof rewardCard !== 'string' &&
          (rewardCard as CardType).actions?.length > 0 && (
            <CardActionsList
              actions={(rewardCard as CardType).actions}
              size="sm"
            />
          )}
      </Card.Body>
    </Card>
  );
};

const FailedTaskCard: React.FC<FailedTaskCardProps> = ({ task }) => {
  const { title } = task;

  return (
    <Card
      text="white"
      className="mb-3"
      style={{
        backgroundColor: 'rgba(128, 128, 128, 0.4)',
        opacity: 0.6,
      }}
    >
      <Card.Body className="d-flex gap-3">
        <div
          className="d-flex align-items-center justify-content-center rounded-3"
          style={{
            width: '3rem',
            height: '3rem',
            flexShrink: 0,
            backgroundColor: 'rgba(108, 117, 125, 0.5)',
          }}
        >
          <XCircle size={32} strokeWidth={2} color="#6c757d" />
        </div>
        <div className="flex-grow-1 d-flex flex-column justify-content-center">
          <h3 className="h5 fw-bold mb-0 text-muted">{title}</h3>
        </div>
      </Card.Body>
    </Card>
  );
};

const ActiveTaskCard: React.FC<ActiveTaskCardProps> = ({ task, icon }) => {
  const [showModal, setShowModal] = useState(false);
  const { title, progress, start, stop, isActive, _id } = task;
  const { t } = useTranslation();

  const normalizedProgress = Math.min(Math.max(progress, 0), 100);

  const formatTimeRemaining = (targetTime: DateTime): string => {
    const now = DateTime.now();
    const localTarget = targetTime.toLocal();
    const diff = localTarget.diff(now, ['days', 'hours', 'minutes']);

    const days = Math.floor(diff.days);
    const hours = Math.floor(diff.hours);
    const minutes = Math.floor(diff.minutes);

    const parts: string[] = [];

    if (days > 0) {
      parts.push(t('tasks.timeUnits.day', { count: days }));
    }

    if (hours > 0) {
      parts.push(t('tasks.timeUnits.hour', { count: hours }));
    }

    if (minutes > 0) {
      parts.push(t('tasks.timeUnits.minute', { count: minutes }));
    }

    const result = parts.slice(0, 2);
    return result.join(' ') || t('tasks.timeUnits.noTime');
  };

  const getTimeIndicator = (): string => {
    if (isActive) {
      return t('tasks.endsIn', { time: formatTimeRemaining(stop) });
    } else {
      return t('tasks.startsIn', { time: formatTimeRemaining(start) });
    }
  };

  const handleCardClick = () => {
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  return (
    <>
      <Card
        text="white"
        className="mb-3 position-relative"
        style={{
          backgroundColor: 'rgba(6, 56, 96, 0.85)',
          cursor: 'pointer',
        }}
        onClick={handleCardClick}
      >
        <Card.Body className="d-flex gap-3">
          <div
            className="d-flex align-items-center justify-content-center rounded-3 bg-primary"
            style={{ width: '3rem', height: '3rem', flexShrink: 0 }}
          >
            <DynamicIcon name={icon} size={32} strokeWidth={1.5} />
          </div>
          <div className="flex-grow-1 d-flex flex-column justify-content-center">
            <h3 className={`h5 fw-bold ${isActive ? 'mb-2' : 'mb-0'}`}>
              {title}
            </h3>
            {isActive && <ProgressBar now={normalizedProgress} />}
          </div>
          {typeof task.rewardCard !== 'string' &&
            (task.rewardCard as CardType).actions?.length > 0 && (
              <CardActionsList
                actions={(task.rewardCard as CardType).actions}
                size="sm"
              />
            )}
        </Card.Body>
        {ClarificationSpan(getTimeIndicator())}
      </Card>
      <TaskDetailsModal
        isOpen={showModal}
        onClose={handleCloseModal}
        taskId={_id}
        icon={icon}
      />
    </>
  );
};

/**
 * Single task card component for task list.
 */
const TaskCard: React.FC<TaskCardProps> = ({ taskId, icon }) => {
  const { getTaskById } = useTasksStore();

  const task = getTaskById(taskId);
  if (!task) return null;

  if (
    task.status === RecurrenceTaskStatusEnum.Failed ||
    (task.status === RecurrenceTaskStatusEnum.InProgress &&
      DateTime.now() >= task.stop &&
      !task.isCompleted)
  ) {
    return <FailedTaskCard task={task} />;
  }

  if (task.status)
    if (
      task.isCompleted ||
      task.status === RecurrenceTaskStatusEnum.CompleteRequest ||
      DateTime.now() >= task.stop
    ) {
      return <CompletedTaskCard task={task} />;
    }

  return <ActiveTaskCard task={task} icon={icon} />;
};

export default TaskCard;
export type { TaskCardProps };
